/* eslint-disable react/prop-types */
import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { Modal, notification, Button, Table, Spin, Card, Tabs, ConfigProvider } from 'antd';
import { ArrowLeftOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import axiosConfig from '../../redux/PQTlogDetails/axiosConfig1';
import './MaterialsWeatherInspectionModal.css';

const { TabPane } = Tabs;

const emptyBundle = () => ({
  materials: [],
  weather: [],
  inspectionEquipment: [],
});

const parseMwResponse = (res) => {
  const body = res?.data;
  const inner = body?.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return {
      materials: Array.isArray(inner.materials) ? inner.materials : [],
      weather: Array.isArray(inner.weather) ? inner.weather : [],
      inspectionEquipment: Array.isArray(inner.inspectionEquipment)
        ? inner.inspectionEquipment
        : [],
    };
  }
  return emptyBundle();
};

const fmtDate = (v) => (v ? moment(v).format('DD-MMM-YYYY') : '—');
const fmtDateTime = (v) => (v ? moment(v).format('DD-MMM-YYYY HH:mm') : '—');
const fmtCell = (v) => (v == null || v === '' ? '—' : String(v));

const MATERIALS_COLUMNS = [
  {
    title: 'Name',
    dataIndex: 'batchDescription',
    key: 'batchDescription',
    ellipsis: true,
    render: fmtCell,
  },
  {
    title: 'BATCH Certificate No',
    dataIndex: 'batchNumber',
    key: 'batchNumber',
    ellipsis: true,
    render: fmtCell,
  },
  {
    title: 'Expiry date',
    dataIndex: 'expirationDate',
    key: 'expirationDate',
    width: 140,
    render: fmtDate,
  },
  { title: 'Added on', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: fmtDateTime },
];

const WEATHER_COLUMNS = [
  { title: 'Condition', dataIndex: 'condition', key: 'condition', ellipsis: true, render: fmtCell },
  {
    title: 'Temperature (°C)',
    dataIndex: 'temperature',
    key: 'temperature',
    width: 140,
    render: fmtCell,
  },
  { title: 'Humidity (%)', dataIndex: 'humidity', key: 'humidity', width: 120, render: fmtCell },
  { title: 'Dew point', dataIndex: 'dewPoint', key: 'dewPoint', width: 100, render: fmtCell },
  { title: 'Added on', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: fmtDateTime },
];

const EQUIPMENT_COLUMNS = [
  { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true, render: fmtCell },
  {
    title: 'Serial number',
    dataIndex: 'serialNumber',
    key: 'serialNumber',
    ellipsis: true,
    render: fmtCell,
  },
  { title: 'Expiry date', dataIndex: 'expiryDate', key: 'expiryDate', width: 140, render: fmtDate },
  { title: 'Added on', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: fmtDateTime },
];

const MaterialsWeatherInspectionModal = forwardRef(
  ({ selectedSOW, logDateRange, onViewDetails, hideTrigger = false }, ref) => {
  const [isMwDetailsModalVisible, setIsMwDetailsModalVisible] = useState(false);
  const [mwBundle, setMwBundle] = useState(emptyBundle);
  const [mwLoading, setMwLoading] = useState(false);
  const [mwDetailsTab, setMwDetailsTab] = useState('environment');

  const fetchMwInspectionEquipment = useCallback(
    async (from, to) => {
      const sowId = selectedSOW?.id;
      if (!sowId || !from || !to) return false;
      setMwLoading(true);
      try {
        const fromDate = moment(from).format('YYYY-MM-DD');
        const toDate = moment(to).format('YYYY-MM-DD');
        const res = await axiosConfig.get('/pqt/sow/materials-weather-inspection-equipment', {
          params: { sowId, fromDate, toDate },
        });
        const bundle = parseMwResponse(res);
        setMwBundle(bundle);
        if (
          bundle.materials.length === 0 &&
          bundle.weather.length === 0 &&
          bundle.inspectionEquipment.length === 0
        ) {
          notification.info({
            message: 'No records',
            description: 'No materials, weather, or inspection equipment for this range.',
          });
        }
        return true;
      } catch (error) {
        console.error('materials-weather-inspection-equipment:', error);
        setMwBundle(emptyBundle());
        notification.error({
          message: 'Failed to load inspection data',
          description: error?.message || error?.error || 'Please try again later.',
        });
        return false;
      } finally {
        setMwLoading(false);
      }
    },
    [selectedSOW]
  );

  const handleViewDetailsClick = useCallback(async () => {
    if (!selectedSOW?.id) {
      notification.warning({
        message: 'Select a pipeline',
        description: 'Choose a SOW (pipeline) before viewing details.',
      });
      return;
    }
    const from = logDateRange?.fromDate;
    const to = logDateRange?.toDate;
    if (!from || !to) {
      notification.warning({
        message: 'Select a date on the calendar',
        description:
          'Open a month or year on the calendar and pick a day (or month) so the log table loads. Dates from that view are used for materials, weather, and inspection equipment.',
      });
      return;
    }
    if (moment(to, 'YYYY-MM-DD').isBefore(moment(from, 'YYYY-MM-DD'), 'day')) {
      notification.warning({
        message: 'Invalid range',
        description: 'End date must be on or after start date.',
      });
      return;
    }
    setMwBundle(emptyBundle());
    setMwDetailsTab('environment');
    onViewDetails?.();
    setIsMwDetailsModalVisible(true);
    const ok = await fetchMwInspectionEquipment(from, to);
    if (!ok) {
      setIsMwDetailsModalVisible(false);
    }
  }, [selectedSOW, logDateRange, onViewDetails, fetchMwInspectionEquipment]);

  useImperativeHandle(
    ref,
    () => ({
      openViewDetails: handleViewDetailsClick,
    }),
    [handleViewDetailsClick]
  );

  const handleMwDetailsClose = () => {
    setIsMwDetailsModalVisible(false);
    setMwBundle(emptyBundle());
    setMwDetailsTab('environment');
  };

  /** `pipeline-table` matches heat profile import; no `h-100` so global `.ant-table-wrapper` height rules do not fight layout. */
  const tableProps = {
    pagination: false,
    className: 'pipeline-table',
    size: 'small',
    style: { width: '100%', minWidth: 0, maxWidth: '100%' },
    scroll: {
      y: 'min(480px, calc(100vh - 320px))',
    },
  };

  return (
    <>
      {!hideTrigger ? (
        <Button type="default" onClick={handleViewDetailsClick} loading={mwLoading}>
          View Details
        </Button>
      ) : null}

      <Modal
        width={960}
        className="heat-profile-modal pipeline-modal mw-inspection-details-modal-shell"
        wrapClassName="mw-inspection-details-modal"
        title=""
        closable={false}
        visible={isMwDetailsModalVisible}
        onCancel={handleMwDetailsClose}
        footer={null}
        destroyOnClose
        maskClosable
        style={{ top: 40 }}
        bodyStyle={{
          padding: 0,
          maxHeight: 'calc(100vh - 100px)',
          overflow: 'auto',
        }}
      >
        <div className="mw-inspection-modal-head">
          <button
            type="button"
            className="mw-inspection-back"
            onClick={handleMwDetailsClose}
            aria-label="Back"
          >
            <ArrowLeftOutlined />
          </button>
          <div className="mw-inspection-head-title">
            <h2>Materials, weather &amp; inspection</h2>
          </div>
          <button
            type="button"
            className="mw-inspection-close-x"
            onClick={handleMwDetailsClose}
            aria-label="Close"
          >
            <CloseOutlined />
          </button>
        </div>
        <ConfigProvider
          getPopupContainer={(triggerNode) =>
            triggerNode?.closest?.('.ant-modal') || document.body
          }
        >
          <div className="mw-inspection-details-table-root">
            <Spin spinning={mwLoading}>
              <Tabs
                activeKey={mwDetailsTab}
                onChange={setMwDetailsTab}
                className="mw-inspection-tabs"
                destroyInactiveTabPane={false}
              >
              <TabPane
                tab={`Environmental Conditions (${mwBundle.weather.length})`}
                key="environment"
              >
                <Card className="mw-inspection-details-card" bodyStyle={{ padding: 0 }}>
                  <Table
                    {...tableProps}
                    columns={WEATHER_COLUMNS}
                    dataSource={mwBundle.weather.map((row) => ({ ...row, key: row.id }))}
                    locale={{ emptyText: 'No environmental condition records' }}
                  />
                </Card>
              </TabPane>
              <TabPane tab={`Materials (${mwBundle.materials.length})`} key="materials">
                <Card className="mw-inspection-details-card" bodyStyle={{ padding: 0 }}>
                  <Table
                    {...tableProps}
                    columns={MATERIALS_COLUMNS}
                    dataSource={mwBundle.materials.map((row) => ({ ...row, key: row.id }))}
                    locale={{ emptyText: 'No materials' }}
                  />
                </Card>
              </TabPane>
              <TabPane
                tab={`Inspection equipment (${mwBundle.inspectionEquipment.length})`}
                key="equipment"
              >
                <Card className="mw-inspection-details-card" bodyStyle={{ padding: 0 }}>
                  <Table
                    {...tableProps}
                    columns={EQUIPMENT_COLUMNS}
                    dataSource={mwBundle.inspectionEquipment.map((row) => ({
                      ...row,
                      key: row.id,
                    }))}
                    locale={{ emptyText: 'No inspection equipment' }}
                  />
                </Card>
              </TabPane>
            </Tabs>
            </Spin>
          </div>
        </ConfigProvider>
      </Modal>
    </>
  );
});

MaterialsWeatherInspectionModal.displayName = 'MaterialsWeatherInspectionModal';

export default MaterialsWeatherInspectionModal;
